import {formatPlannerSidebarTime, plannerEventDate} from './plannerDateTime';
import {patientDisplayNameFromRecord} from '../../utils/patientNames';
import {
  buildPlannerEvent,
  clean,
  plannerPatientFromPatientItem,
  plannerPatientItemIdentityKeys,
} from './plannerModels';

export const PLANNER_PATIENT_VIEW_MODES = Object.freeze({
  schedule: 'schedule',
  patients: 'patients',
  completed: 'completed',
  unlinked: 'unlinked',
  phone: 'phone',
});

function patientItemSpecificity(item: any) {
  return [
    item?.localPatient ? 1 : 0,
    item?.ehrPatient ? 1 : 0,
    item?.lastSessionAt ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);
}

export function plannerPatientItemDisplayName(item: any) {
  const patient = plannerPatientFromPatientItem(item);
  return (
    patientDisplayNameFromRecord(patient || item || {}) || 'Unknown patient'
  );
}

const patientItemLinkedEhrId = (item: any) =>
  clean(
    item?.ehrPatient?.ehrSourceId ||
      item?.ehrPatient?.ehr_source_id ||
      item?.ehrPatient?.id ||
      item?.localPatient?.fhir_patient_id ||
      item?.localPatient?.ehr_patient_id ||
      item?.localPatient?.ehrPatientId ||
      item?.localPatient?._ehrPatientId ||
      item?.localPatient?._linkedEhrId,
  );

const patientItemSortName = (item: any) =>
  (clean(item?.sortName) || plannerPatientItemDisplayName(item)).toLowerCase();

const localPatientIdForItem = (item: any) => clean(item?.localPatient?.id);

const localPatientIdForEvent = (event: any) => {
  const patient = event?.patient || {};
  const localId = clean(patient.localId);
  if (localId) return localId;
  const id = clean(patient.id);
  return id.startsWith('local_') ? id.slice('local_'.length) : '';
};

export function buildLocalPatientBrowserItems(items: any[] = []) {
  return dedupePlannerPatientItems(items)
    .filter(
      item =>
        item?.localPatient &&
        !item?.ehrPatient &&
        !patientItemLinkedEhrId(item),
    )
    .sort(
      (left, right) =>
        patientItemSortName(left).localeCompare(
          patientItemSortName(right),
          undefined,
          {sensitivity: 'base'},
        ) ||
        plannerPatientItemDisplayName(left).localeCompare(
          plannerPatientItemDisplayName(right),
          undefined,
          {sensitivity: 'base'},
        ) ||
        clean(left?.id).localeCompare(clean(right?.id), undefined, {
          sensitivity: 'base',
        }),
    );
}

export function buildLocalPatientBrowserSections(
  items: any[] = [],
  encounterEvents: any[] = [],
  {recentLimit = 10}: Record<string, any> = {},
) {
  const alphabeticalItems = buildLocalPatientBrowserItems(items);
  const itemByLocalId = new Map();
  alphabeticalItems.forEach(item => {
    const localId = localPatientIdForItem(item);
    if (localId) itemByLocalId.set(localId, item);
  });
  const latestEncounterByLocalId = new Map();

  encounterEvents.forEach((event, index) => {
    const localId = localPatientIdForEvent(event);
    if (!localId || !itemByLocalId.has(localId)) return;
    const encounteredAt = plannerEventDate(event);
    const encounteredTime = encounteredAt?.getTime?.();
    if (
      typeof encounteredTime !== 'number' ||
      !Number.isFinite(encounteredTime)
    )
      return;
    const existing = latestEncounterByLocalId.get(localId);
    if (existing && existing.encounteredTime >= encounteredTime) return;
    latestEncounterByLocalId.set(localId, {
      encounteredTime,
      index,
      item: itemByLocalId.get(localId),
    });
  });

  const recentItems = [...latestEncounterByLocalId.values()]
    .sort(
      (left, right) =>
        right.encounteredTime - left.encounteredTime ||
        left.index - right.index,
    )
    .slice(0, Math.max(0, recentLimit))
    .map(entry => entry.item)
    .filter(Boolean);
  const recentLocalIds = new Set(
    recentItems.map(localPatientIdForItem).filter(Boolean),
  );

  return {
    recentItems,
    alphabeticalItems: alphabeticalItems.filter(
      item => !recentLocalIds.has(localPatientIdForItem(item)),
    ),
  };
}

export function dedupePlannerPatientItems(items: any[] = []) {
  const result: any[] = [];
  const indexByKey = new Map();

  for (const item of items.filter(Boolean)) {
    const keys = plannerPatientItemIdentityKeys(item);
    const existingIndex = keys
      .map(key => indexByKey.get(key))
      .find(index => index != null);

    if (existingIndex == null) {
      const nextIndex = result.length;
      result.push(item);
      keys.forEach(key => indexByKey.set(key, nextIndex));
      continue;
    }

    const existing = result[existingIndex];
    if (patientItemSpecificity(item) > patientItemSpecificity(existing)) {
      result[existingIndex] = item;
    }
    keys.forEach(key => indexByKey.set(key, existingIndex));
  }

  return result;
}

function plannerPatientSelectionEventId(item: any, source = 'patient') {
  const patient = plannerPatientFromPatientItem(item);
  const sourceKey = clean(source) || 'patient';
  return `patient-${sourceKey}-${patient?.id || clean(item?.id) || 'unknown'}`;
}

export function buildPlannerPatientSelectionEvent(
  item: any,
  {source = 'patient'}: Record<string, any> = {},
) {
  const patient = plannerPatientFromPatientItem(item);
  if (!patient?.id) return null;
  const metadata = [
    patient?.mrn ? `MRN ${patient.mrn}` : '',
    patient?.accountNo ? `Acct ${patient.accountNo}` : '',
    patient?.pcp ? `PCP ${patient.pcp}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return buildPlannerEvent({
    id: plannerPatientSelectionEventId(item, source),
    stage: 'patient',
    source,
    sourceId: patient.id,
    patient,
    patientName: plannerPatientItemDisplayName(item),
    time: formatPlannerSidebarTime(item?.lastSessionAt),
    sortDate: item?.lastSessionAt || null,
    language: patient.language,
    detailLine: metadata,
    statusLabel: 'Patient',
  });
}
